import { Component, HostListener, OnInit } from '@angular/core';
import { ClinicalEncounterService } from '@app/core/services/clinical-encounter.service';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { NoraRecorderService } from './nora-recorder.service';
import { AuthService, UiStateService } from '@app/core/services';
import { combineLatest } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';
import * as _ from 'lodash';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-nora-recorder',
  templateUrl: './nora-recorder.component.html',
  styleUrls: ['./nora-recorder.component.scss'],
})
export class NoraRecorderComponent implements OnInit {
  position: { x: number; y: number } = { x: 260, y: 122 };

  params$ = combineLatest([this.authService.currentTenantId$, this.uiStateService.currentPatientId$]).pipe(
    map(([tenantId, patientId]) => ({ tenantId, patientId })),
    distinctUntilChanged()
  );
  encounter$ = this.params$.pipe(
    switchMap(params => this.clinicalEncounterService.encounterInProgress$(params.tenantId, params.patientId)),
    untilDestroyed(this),
    shareReplay(1)
  );

  private recorder: any = null;
  recorderConfig: any;
  private scriptLoaded: boolean = false;

  constructor(
    private clinicalEncounterService: ClinicalEncounterService,
    private noraRecorderService: NoraRecorderService,
    private authService: AuthService,
    private uiStateService: UiStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.encounter$
      .pipe(
        take(1),
        map(encounter => {
          const currentUrl = this.router.url;
          const isConsultRoute = currentUrl.includes('/consult');

          if (!isConsultRoute) {
            this.uiStateService.toggleNoraRecorder();
          }

          return { encounter, isConsultRoute };
        }),
        filter(({ isConsultRoute }) => isConsultRoute),
        withLatestFrom(this.params$),
        tap(() => {
          this.noraRecorderService.setIsMountingRecorder(true);
        }),
        switchMap(([{ encounter }, params]) => {
          return this.clinicalEncounterService.clinicalNoteById$(params.tenantId, params.patientId).pipe(
            take(1),
            map(clinicalNote => ({ clinicalNote, params, encounter }))
          );
        }),
        switchMap(({ encounter, params, clinicalNote }) =>
          this.noraRecorderService.getConfiguration(params.tenantId, encounter.EncounterId).pipe(
            tap(recorderConfig => {
              this.noraRecorderService.setRecordingPatientId(encounter.PatientId);

              this.loadScript(
                recorderConfig.apiKey,
                recorderConfig.doctorID,
                recorderConfig.consultationID,
                recorderConfig.apiBaseUrl,
                clinicalNote
              );

              this.setupEventListeners();
            })
          )
        )
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.cleanupRecorder();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload($event: any): void {
    $event.returnValue = false;
    this.uiStateService.noraRecorderVisible$
      .pipe(
        take(1),
        tap(visible => {
          if (visible) {
            this.cleanupRecorder();
            this.uiStateService.toggleNoraRecorder();
          }
        }),
        map(() => true)
      )
      .subscribe();
  }

  private async loadScript(apiKey: string, doctorID: string, consultationID: string, apiBaseUrl: string, clinicalNote: string) {
    if (window['NoraRecorder']) {
      this.scriptLoaded = true;
      await this.initializeRecorder(apiKey, doctorID, consultationID, apiBaseUrl, clinicalNote);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.noraV2.noraModuleUrl;

    script.async = true;

    script.onload = async () => {
      this.scriptLoaded = true;

      await this.initializeRecorder(apiKey, doctorID, consultationID, apiBaseUrl, clinicalNote);
    };

    script.onerror = e => {
      console.error('Failed to load NoraRecorder script', e);
      this.noraRecorderService.setIsMountingRecorder(false);
    };

    document.body.appendChild(script);
  }

  private async initializeRecorder(apiKey: string, doctorID: string, consultationID: string, apiBaseUrl: string, clinicalNote: string) {
    if (!this.scriptLoaded || !window['NoraRecorder']) {
      console.error('NoraRecorder script not loaded');
      return;
    }

    if (!apiKey || !doctorID || !consultationID || !apiBaseUrl) {
      console.error('Missing required parameters for NoraRecorder');
      return;
    }

    window['NoraRecorder'].ready(async () => {
      const element = document.getElementById('nora-recorder-toggle');

      if (element) {
        const rect = element.getBoundingClientRect();
        const x = rect.left + 30;
        const y = rect.top - 22;

        this.position = { x, y };
      }

      const recorder = await window['NoraRecorder']({
        apiKey,
        doctorID,
        consultationID,
        apiBaseUrl,
        primaryColor: '#F7941C',
        position: this.position,
        snackbarPosition: 'below-component',
        enableContextChecker: false,
        prePromptCallback: () => clinicalNote,
      });

      this.recorder = recorder;
      this.noraRecorderService.registerRecorder(consultationID, recorder);
    });
  }

  private cleanupRecorder(): void {
    if (this.recorder) {
      try {
        this.noraRecorderService.setIsMountingRecorder(false);
        window['NoraRecorder']?.cleanup();
        this.recorder.cleanup();
        this.recorder = null;

        document.removeEventListener('nora-recorder-status', this.handleStatusEvents);
        document.removeEventListener('nora-recorder-content', this.handleContentEvent);
        this.noraRecorderService.setIsRecording(false);

        this.encounter$
          .pipe(
            take(1),
            tap(encounter => this.noraRecorderService.unregisterRecorder(encounter.EncounterId))
          )
          .subscribe();
      } catch (err) {
        console.error('Error cleaning up recorder:', err);
      }
    }
  }

  private setupEventListeners(): void {
    document.addEventListener('nora-recorder-status', this.handleStatusEvents);

    document.addEventListener('nora-recorder-content', this.handleContentEvent);
  }

  private handleStatusEvents = (event: CustomEvent) => {
    if (event?.detail?.type === 'recorder-ready') {
      this.noraRecorderService.setIsMountingRecorder(false);
    }

    if (event?.detail?.type === 'recording-started') {
      this.noraRecorderService.setIsRecording(true);
    }

    if (event?.detail?.type === 'recording-discarded') {
      this.noraRecorderService.setIsRecording(false);
    }

    if (event?.detail?.type === 'close-request') {
      this.uiStateService.toggleNoraRecorder();
    }
  };

  private handleContentEvent = (event: CustomEvent) => {
    if (event?.detail.type === 'content-ready' && this.noraRecorderService.isRecording$.value) {
      this.noraRecorderService.setIsRecording(false);
      const content = JSON.parse(event.detail.content).ops[0].insert;
      this.clinicalEncounterService.inProgressEncounters$
        .pipe(
          take(1),
          tap(encounters => {
            const encounter = encounters.find(encounter => encounter.EncounterId === event.detail.consultationID);

            this.clinicalEncounterService.updateClinicalNote(event.detail.doctorID, encounter.PatientId, content);
          })
        )
        .subscribe();
    }
  };
}
